import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  buildJobsMonitorQueryArgs,
  computeFailureRatePercent,
  countJobStatuses,
} from "@/pages/AdminV2/jobsMonitorUtils";
import { useGetAdminV2JobsQuery } from "@/store/api/baseApi";
import type { AdminV2Job, AdminV2JobStatus } from "@/types/adminV2";
import { getErrorMessage, toError } from "@/utils/errorHandling";
import FrontendLoggerFactory from "@/utils/logging/FrontendLoggerFactory";

const DEBOUNCE_MS = 250;
const JOB_STATUS_FILTERS: ReadonlySet<AdminV2JobStatus | "all"> = new Set([
  "all",
  "queued",
  "running",
  "succeeded",
  "failed",
  "cancelled",
]);

const logger =
  FrontendLoggerFactory.getLogger().withComponent("JobsMonitorPage");

const parseJobStatusFilterFromSearch = (
  statusFilter: string | null,
): AdminV2JobStatus | "all" => {
  if (!statusFilter) {
    return "all";
  }

  return JOB_STATUS_FILTERS.has(statusFilter as AdminV2JobStatus | "all")
    ? (statusFilter as AdminV2JobStatus | "all")
    : "all";
};

const parsePositiveIntegerFromSearch = (
  value: string | null,
): number | null => {
  if (!value) {
    return null;
  }

  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

export interface JobsMonitorPageState {
  statusFilter: AdminV2JobStatus | "all";
  jobTypeFilter: string;
  dashboardWindowHours: number | null;
  dashboardSource: string | null;
  hasActiveFilters: boolean;
  isLoading: boolean;
  isError: boolean;
  jobs: AdminV2Job[];
  onStatusFilterChange: (value: AdminV2JobStatus | "all") => void;
  onJobTypeFilterChange: (value: string) => void;
  onClearFilters: () => void;
  refetch: () => Promise<unknown>;
}

export const useJobsMonitorPageState = (): JobsMonitorPageState => {
  const initialSearchParams = useMemo(
    () => new URLSearchParams(window.location.search),
    [],
  );
  const initialStatusFilter = useMemo(
    () => parseJobStatusFilterFromSearch(initialSearchParams.get("status")),
    [initialSearchParams],
  );
  const initialJobTypeFilter = useMemo(
    () => initialSearchParams.get("jobType")?.trim() ?? "",
    [initialSearchParams],
  );
  const dashboardWindowHours = useMemo(
    () =>
      parsePositiveIntegerFromSearch(initialSearchParams.get("windowHours")),
    [initialSearchParams],
  );
  const dashboardSource = useMemo(
    () => initialSearchParams.get("source")?.trim() ?? null,
    [initialSearchParams],
  );

  const [statusFilter, setStatusFilter] = useState<AdminV2JobStatus | "all">(
    initialStatusFilter,
  );
  const [jobTypeFilter, setJobTypeFilter] = useState(initialJobTypeFilter);

  const navigationToken = useId();
  const navigationId = `jobs-monitor-${navigationToken.replace(/:/g, "")}`;
  const pageLoadStartedAtRef = useRef<number>(0);
  const queryStartAtRef = useRef<number | null>(null);
  const lastQuerySignatureRef = useRef<string>("");
  const lastReadySignatureRef = useRef<string>("");
  const lastFilterSignatureRef = useRef<string>("");
  const lastDebouncedJobTypeRef = useRef<string>("");
  const lastLoadErrorMessageRef = useRef<string | null>(null);
  const jobTypeDebounceRef = useRef<number | null>(null);
  const wasLoadingRef = useRef<boolean>(false);

  useEffect(() => {
    pageLoadStartedAtRef.current = Date.now();
    logger.debug("JOBS_MONITOR: page enter", {
      feature: "FND",
      navigationId,
      operation: "JOBS_MONITOR_PAGE_ENTER",
      component: "JobsMonitorPage",
    });
    return () => {
      logger.debug("JOBS_MONITOR: page exit", {
        feature: "FND",
        navigationId,
        operation: "JOBS_MONITOR_PAGE_EXIT",
        component: "JobsMonitorPage",
        durationMs: Date.now() - pageLoadStartedAtRef.current,
      });
    };
  }, [navigationId]);

  useEffect(() => {
    const filterSignature = `${statusFilter}:${jobTypeFilter}`;
    if (filterSignature === lastFilterSignatureRef.current) {
      logger.debug("JOBS_MONITOR: filter signature unchanged", {
        feature: "FND",
        operation: "JOBS_MONITOR_FILTER_SIGNATURE_UNCHANGED",
        component: "JobsMonitorPage",
        statusFilter,
        hasJobTypeFilter: jobTypeFilter.trim().length > 0,
      });
      return;
    }

    lastFilterSignatureRef.current = filterSignature;
    logger.debug("JOBS_MONITOR: filter signature changed", {
      feature: "FND",
      navigationId,
      operation: "JOBS_MONITOR_FILTER_SIGNATURE_CHANGED",
      component: "JobsMonitorPage",
      statusFilter,
      jobTypeLength: jobTypeFilter.trim().length,
    });
  }, [jobTypeFilter, navigationId, statusFilter]);

  const queryArgs = useMemo(
    () =>
      buildJobsMonitorQueryArgs({
        statusFilter,
        jobTypeFilter,
      }),
    [statusFilter, jobTypeFilter],
  );
  const hasActiveFilters =
    statusFilter !== "all" || jobTypeFilter.trim().length > 0;

  const querySignature = useMemo(() => JSON.stringify(queryArgs), [queryArgs]);

  const { data, isLoading, isFetching, isError, error, refetch } =
    useGetAdminV2JobsQuery(queryArgs);
  const jobsData = data?.jobs;
  const jobs = useMemo(() => jobsData ?? [], [jobsData]);
  const statusCounts = countJobStatuses(jobs);
  const cancelRequestedCount = jobs.filter((job) => job.cancelRequested).length;
  const failedCount = statusCounts.failed;
  const runningOrQueuedCount = statusCounts.running + statusCounts.queued;
  const getQueryDurationMs = () =>
    queryStartAtRef.current ? Date.now() - queryStartAtRef.current : undefined;

  useEffect(() => {
    if (querySignature !== lastQuerySignatureRef.current) {
      lastQuerySignatureRef.current = querySignature;
      queryStartAtRef.current = Date.now();
      logger.debug("JOBS_MONITOR: query args built", {
        feature: "FND",
        navigationId,
        operation: "JOBS_MONITOR_QUERY_ARGS_BUILD",
        component: "JobsMonitorPage",
        statusFilter,
        jobTypeFilter: jobTypeFilter.trim(),
        querySignature,
      });
      logger.debug("JOBS_MONITOR: query started", {
        feature: "FND",
        navigationId,
        operation: "JOBS_MONITOR_QUERY_START",
        component: "JobsMonitorPage",
        limit: queryArgs.limit,
        statusFilter,
        hasJobTypeFilter: jobTypeFilter.trim().length > 0,
        querySignature,
      });
    }
  }, [
    navigationId,
    queryArgs.limit,
    querySignature,
    jobTypeFilter,
    statusFilter,
  ]);

  useEffect(() => {
    if (isLoading && !wasLoadingRef.current) {
      logger.debug("JOBS_MONITOR: query loading", {
        feature: "FND",
        navigationId,
        operation: "JOBS_MONITOR_QUERY_LOADING_START",
        component: "JobsMonitorPage",
        statusFilter,
      });
      logger.debug("JOBS_MONITOR: loading indicator shown", {
        feature: "FND",
        navigationId,
        operation: "JOBS_MONITOR_LOADING_INDICATOR_SHOWN",
        component: "JobsMonitorPage",
        jobTypeFilter: jobTypeFilter.trim(),
        querySignature,
      });
    }

    if (!isLoading && wasLoadingRef.current) {
      logger.debug("JOBS_MONITOR: query loading complete", {
        feature: "FND",
        navigationId,
        operation: "JOBS_MONITOR_QUERY_LOADING_COMPLETE",
        component: "JobsMonitorPage",
        durationMs: getQueryDurationMs(),
        hasResults: jobs.length > 0,
      });
      logger.debug("JOBS_MONITOR: loading indicator hidden", {
        feature: "FND",
        navigationId,
        operation: "JOBS_MONITOR_LOADING_INDICATOR_HIDDEN",
        component: "JobsMonitorPage",
        totalJobs: jobs.length,
      });
    }

    wasLoadingRef.current = isLoading;
  }, [
    isLoading,
    jobs.length,
    jobTypeFilter,
    navigationId,
    querySignature,
    statusFilter,
  ]);

  useEffect(() => {
    if (isError) {
      const message = getErrorMessage(error);
      if (message !== lastLoadErrorMessageRef.current) {
        lastLoadErrorMessageRef.current = message;
        logger.error("JOBS_MONITOR: query failed", toError(error), {
          feature: "FND",
          navigationId,
          operation: "JOBS_MONITOR_QUERY_FAIL",
          component: "JobsMonitorPage",
          statusFilter,
          hasJobTypeFilter: jobTypeFilter.trim().length > 0,
          errorMessage: message,
          querySignature,
        });
        logger.debug("JOBS_MONITOR: query fail branch entered", {
          feature: "FND",
          navigationId,
          operation: "JOBS_MONITOR_QUERY_FAIL_BRANCH",
          component: "JobsMonitorPage",
          querySignature,
          hasResults: jobs.length > 0,
        });
      }
      return;
    }

    if (lastLoadErrorMessageRef.current !== null) {
      logger.debug("JOBS_MONITOR: query recovery", {
        feature: "FND",
        navigationId,
        operation: "JOBS_MONITOR_QUERY_ERROR_RECOVERY",
        component: "JobsMonitorPage",
        statusFilter,
        jobTypeFilter: jobTypeFilter.trim(),
      });
      lastLoadErrorMessageRef.current = null;
    }

    if (isLoading || isFetching) {
      return;
    }

    const readySignature = `${querySignature}:${jobs.length}:${isError}`;
    if (readySignature === lastReadySignatureRef.current) {
      logger.debug("JOBS_MONITOR: query ready signature unchanged", {
        feature: "FND",
        operation: "JOBS_MONITOR_QUERY_READY_SIGNATURE_UNCHANGED",
        component: "JobsMonitorPage",
        querySignature,
        statusFilter,
      });
      return;
    }
    lastReadySignatureRef.current = readySignature;

    logger.debug("JOBS_MONITOR: query ready", {
      feature: "FND",
      navigationId,
      operation: "JOBS_MONITOR_QUERY_READY",
      component: "JobsMonitorPage",
      statusFilter,
      hasFilters: jobTypeFilter.trim().length > 0,
      jobTypeFilter: jobTypeFilter.trim(),
      totalJobs: jobs.length,
      durationMs: getQueryDurationMs(),
    });
    logger.debug("JOBS_MONITOR: page ready", {
      feature: "FND",
      navigationId,
      operation: "JOBS_MONITOR_PAGE_READY",
      component: "JobsMonitorPage",
      statusFilter,
      hasResults: jobs.length > 0,
    });

    if (jobs.length === 0) {
      logger.debug("JOBS_MONITOR: query empty", {
        feature: "FND",
        navigationId,
        operation: "JOBS_MONITOR_QUERY_EMPTY",
        component: "JobsMonitorPage",
        statusFilter,
        hasJobTypeFilter: jobTypeFilter.trim().length > 0,
      });
      logger.debug("JOBS_MONITOR: empty state rendered", {
        feature: "FND",
        navigationId,
        operation: "JOBS_MONITOR_EMPTY_STATE_RENDERED",
        component: "JobsMonitorPage",
        statusFilter,
      });
      return;
    }

    logger.debug("JOBS_MONITOR: query status summary", {
      feature: "FND",
      navigationId,
      operation: "JOBS_MONITOR_QUERY_STATUS_SUMMARY",
      component: "JobsMonitorPage",
      totalJobs: jobs.length,
      queued: statusCounts.queued,
      running: statusCounts.running,
      succeeded: statusCounts.succeeded,
      failed: statusCounts.failed,
      cancelled: statusCounts.cancelled,
      failedPercent: computeFailureRatePercent(failedCount, jobs.length),
    });

    logger.debug("JOBS_MONITOR: list summary", {
      feature: "FND",
      navigationId,
      operation: "JOBS_MONITOR_LIST_SUMMARY",
      component: "JobsMonitorPage",
      totalJobs: jobs.length,
      cancelRequestedCount,
      runningOrQueuedCount,
      failedCount,
    });

    if (cancelRequestedCount > 0) {
      logger.debug("JOBS_MONITOR: cancel request present", {
        feature: "FND",
        navigationId,
        operation: "JOBS_MONITOR_CANCEL_REQUEST_PRESENT",
        component: "JobsMonitorPage",
        cancelRequestedCount,
      });
    } else {
      logger.debug("JOBS_MONITOR: no cancel requests", {
        feature: "FND",
        navigationId,
        operation: "JOBS_MONITOR_NO_CANCEL_REQUESTS",
        component: "JobsMonitorPage",
        totalJobs: jobs.length,
      });
    }

    if (statusFilter === "all" && runningOrQueuedCount > 20) {
      logger.warn("JOBS_MONITOR: backlog warning", {
        feature: "FND",
        navigationId,
        operation: "JOBS_MONITOR_HIGH_BACKLOG",
        component: "JobsMonitorPage",
        runningOrQueuedCount,
        statusFilter,
      });
    }

    if (failedCount > 0) {
      logger.warn("JOBS_MONITOR: failed jobs detected", {
        feature: "FND",
        navigationId,
        operation: "JOBS_MONITOR_FAILED_JOBS_PRESENT",
        component: "JobsMonitorPage",
        failedCount,
        failedPercent: computeFailureRatePercent(failedCount, jobs.length),
      });
    }

    logger.debug("JOBS_MONITOR: jobs rendered", {
      feature: "FND",
      navigationId,
      operation: "JOBS_MONITOR_JOBS_RENDERED",
      component: "JobsMonitorPage",
      renderedJobCount: jobs.length,
      statusFilter,
    });
  }, [
    cancelRequestedCount,
    error,
    failedCount,
    isError,
    isFetching,
    isLoading,
    jobs,
    querySignature,
    runningOrQueuedCount,
    statusCounts.cancelled,
    statusCounts.failed,
    statusCounts.queued,
    statusCounts.running,
    statusCounts.succeeded,
    statusFilter,
    jobTypeFilter,
    navigationId,
  ]);

  useEffect(
    () => () => {
      if (jobTypeDebounceRef.current) {
        window.clearTimeout(jobTypeDebounceRef.current);
      }
    },
    [],
  );

  useEffect(() => {
    if (jobTypeDebounceRef.current) {
      window.clearTimeout(jobTypeDebounceRef.current);
    }

    jobTypeDebounceRef.current = window.setTimeout(() => {
      const normalized = jobTypeFilter.trim();

      if (normalized === lastDebouncedJobTypeRef.current) {
        logger.debug("JOBS_MONITOR: job type query unchanged", {
          feature: "FND",
          navigationId,
          operation: "JOBS_MONITOR_JOBTYPE_QUERY_UNCHANGED",
          component: "JobsMonitorPage",
          normalizedLength: normalized.length,
        });
        return;
      }

      lastDebouncedJobTypeRef.current = normalized;
      logger.debug("JOBS_MONITOR: job type filter applied", {
        feature: "FND",
        navigationId,
        operation: "JOBS_MONITOR_JOBTYPE_FILTER_APPLIED",
        component: "JobsMonitorPage",
        jobTypeLength: normalized.length,
        hasQuery: normalized.length > 0,
      });

      if (normalized.length === 0) {
        logger.debug("JOBS_MONITOR: job type filter cleared", {
          feature: "FND",
          navigationId,
          operation: "JOBS_MONITOR_JOBTYPE_FILTER_CLEARED",
          component: "JobsMonitorPage",
          previousLength: jobTypeFilter.length,
        });
        return;
      }

      if (normalized.length > 80) {
        logger.warn("JOBS_MONITOR: long job type filter", {
          feature: "FND",
          navigationId,
          operation: "JOBS_MONITOR_JOBTYPE_QUERY_LONG",
          component: "JobsMonitorPage",
          normalizedLength: normalized.length,
        });
      }
    }, DEBOUNCE_MS);
  }, [jobTypeFilter, navigationId]);

  const onStatusFilterChange = useCallback(
    (nextFilter: AdminV2JobStatus | "all") => {
      if (nextFilter === statusFilter) {
        logger.warn("JOBS_MONITOR: status filter unchanged", {
          feature: "FND",
          navigationId,
          operation: "JOBS_MONITOR_STATUS_FILTER_UNCHANGED",
          component: "JobsMonitorPage",
          statusFilter,
        });
        return;
      }

      logger.debug("JOBS_MONITOR: status filter request", {
        feature: "FND",
        navigationId,
        operation: "JOBS_MONITOR_STATUS_FILTER_REQUEST",
        component: "JobsMonitorPage",
        currentStatusFilter: statusFilter,
        requestedStatus: nextFilter,
        source: "user",
      });

      logger.debug("JOBS_MONITOR: status filter changed", {
        feature: "FND",
        navigationId,
        operation: "JOBS_MONITOR_STATUS_FILTER_CHANGED",
        component: "JobsMonitorPage",
        previousStatusFilter: statusFilter,
        nextStatusFilter: nextFilter,
        source: "user",
      });

      if (nextFilter === "all") {
        logger.debug("JOBS_MONITOR: status filter reset", {
          feature: "FND",
          navigationId,
          operation: "JOBS_MONITOR_STATUS_FILTER_RESET",
          component: "JobsMonitorPage",
          statusFilter: nextFilter,
          source: "user",
        });
      }

      setStatusFilter(nextFilter);
    },
    [navigationId, statusFilter],
  );

  const onJobTypeFilterChange = useCallback(
    (nextJobType: string) => {
      if (nextJobType === jobTypeFilter) {
        logger.debug("JOBS_MONITOR: job type filter unchanged", {
          feature: "FND",
          navigationId,
          operation: "JOBS_MONITOR_JOBTYPE_FILTER_UNCHANGED",
          component: "JobsMonitorPage",
          jobTypeLength: nextJobType.length,
        });
        return;
      }

      logger.debug("JOBS_MONITOR: job type filter request", {
        feature: "FND",
        navigationId,
        operation: "JOBS_MONITOR_JOBTYPE_FILTER_REQUEST",
        component: "JobsMonitorPage",
        jobTypeLength: nextJobType.length,
        source: "user",
      });
      setJobTypeFilter(nextJobType);
    },
    [jobTypeFilter, navigationId],
  );

  const onClearFilters = useCallback(() => {
    if (!hasActiveFilters) {
      logger.debug("JOBS_MONITOR: filters reset skipped", {
        feature: "FND",
        navigationId,
        operation: "JOBS_MONITOR_FILTERS_RESET_SKIPPED",
        component: "JobsMonitorPage",
      });
      return;
    }

    logger.debug("JOBS_MONITOR: filters reset", {
      feature: "FND",
      navigationId,
      operation: "JOBS_MONITOR_FILTERS_RESET",
      component: "JobsMonitorPage",
      source: "user",
      previousStatusFilter: statusFilter,
      previousJobTypeFilter: jobTypeFilter.trim(),
    });
    setStatusFilter("all");
    setJobTypeFilter("");
  }, [hasActiveFilters, jobTypeFilter, navigationId, statusFilter]);

  return {
    statusFilter,
    jobTypeFilter,
    dashboardWindowHours,
    dashboardSource,
    hasActiveFilters,
    isLoading,
    isError,
    jobs,
    onStatusFilterChange,
    onJobTypeFilterChange,
    onClearFilters,
    refetch,
  };
};
